import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { unitUpdateSchema } from '@app/shared';
import type { UnitDto, UnitStatus, UnitUpdateInput } from '@app/shared';
import { AgentChainSelect, deepestAgent } from '../../components/AgentChainSelect';
import type { AgentChain } from '../../components/AgentChainSelect';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { FormDialog } from '../../components/dialogs';
import { SelectInput, TextInput, YesNoInput, applyServerErrors } from '../../components/form';
import { formatDateTime } from '../../lib/format';
import { usePlanOptions } from '../plans/lookups';
import { useChangeReasonOptions, useProductModelOptions, useUnit, useUpdateUnit } from './api';
import type { UnitDetail } from './api';
import { addFieldError } from './formUtils';
import {
  REASON_REQUIRED_STATUSES,
  describeEvent,
  imeiOf,
  productLabel,
  simOf,
  statusOptions,
  storeType,
} from './labels';

const TITLE = 'Update Single Unit';

interface FormValues {
  modelId: string;
  status: UnitStatus;
  reasonId: string | null;
  zip: string;
  puk2: string;
  poNumber: string;
  reservedForPortin: boolean;
  planId: string | null;
}

const toForm = (unit: UnitDto): FormValues => ({
  modelId: unit.model.id,
  status: unit.status,
  reasonId: null,
  zip: unit.zip ?? '',
  puk2: unit.puk2,
  poNumber: unit.poNumber,
  reservedForPortin: unit.reservedForPortin,
  planId: unit.plan?.id ?? null,
});

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography>{value || '-'}</Typography>
    </Box>
  );
}

function UnitForm({ detail, onClose }: { detail: UnitDetail; onClose: () => void }) {
  const { unit, events } = detail;
  const notify = useNotify();
  const models = useProductModelOptions();
  const plans = usePlanOptions();
  const reasons = useChangeReasonOptions();
  const updateUnit = useUpdateUnit();
  // The new owner. Empty means "keep the current one".
  const [chain, setChain] = useState<AgentChain>({});

  const resolver = useMemo<Resolver<FormValues>>(() => {
    const base = zodResolver(unitUpdateSchema) as Resolver<FormValues>;
    return async (values, context, options) => {
      const result = await base(values, context, options);
      const needsReason =
        values.status !== unit.status && REASON_REQUIRED_STATUSES.includes(values.status);
      return needsReason && !values.reasonId
        ? addFieldError(result, 'reasonId', 'Select a change reason')
        : result;
    };
  }, [unit.status]);

  const { control, handleSubmit, setError, formState } = useForm<FormValues>({
    defaultValues: toForm(unit),
    resolver,
  });
  const status = useWatch({ control, name: 'status' });
  const statusChanged = status !== unit.status;

  // The current product may have been deactivated since; keep it selectable so the form shows it.
  const modelOptions = (models.data ?? []).map((m) => ({ value: m.id, label: productLabel(m) }));
  if (!modelOptions.some((o) => o.value === unit.model.id))
    modelOptions.unshift({ value: unit.model.id, label: productLabel(unit.model) });

  const submit = handleSubmit(async (parsed) => {
    // `parsed` is the schema output; an empty ZIP arrives as undefined and is left alone.
    const values = parsed as Partial<FormValues>;
    const input: UnitUpdateInput = {};
    const newOwner = deepestAgent(chain);
    if (newOwner && newOwner !== unit.owner?.id) input.ownerUserId = newOwner;
    if (values.modelId && values.modelId !== unit.model.id) input.modelId = values.modelId;
    if (values.status && values.status !== unit.status) {
      input.status = values.status;
      if (values.reasonId) input.reasonId = values.reasonId;
    }
    if (values.zip && values.zip !== unit.zip) input.zip = values.zip;
    if (values.puk2 !== undefined && values.puk2 !== unit.puk2) input.puk2 = values.puk2;
    if (values.poNumber !== undefined && values.poNumber !== unit.poNumber)
      input.poNumber = values.poNumber;
    if (
      values.reservedForPortin !== undefined &&
      values.reservedForPortin !== unit.reservedForPortin
    )
      input.reservedForPortin = values.reservedForPortin;
    if (values.planId !== undefined && values.planId !== (unit.plan?.id ?? null))
      input.planId = values.planId;

    if (Object.keys(input).length === 0) {
      notify.info('Nothing was changed.');
      return;
    }
    try {
      await updateUnit.mutateAsync({ id: unit.id, input });
      notify.success('Unit updated.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  // `ownerUserId` is not a form field, so its server error is read from the form state by name.
  const ownerError = (formState.errors as Record<string, { message?: string } | undefined>)
    .ownerUserId?.message;

  return (
    <FormDialog
      open
      title={TITLE}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={updateUnit.isPending}
      submitLabel="Update Unit"
      maxWidth="md"
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
          mb: 2,
        }}
      >
        <ReadOnly
          label="SIM / ESN"
          value={`${simOf(unit)}${unit.kind === 'sim' && unit.isEsim ? ' (eSIM)' : ''}`}
        />
        <ReadOnly label="IMEI" value={imeiOf(unit)} />
        <ReadOnly label="Carrier" value={unit.carrier.code} />
        <ReadOnly label="MDN" value={unit.mdn ?? ''} />
        <ReadOnly label="Store Type" value={storeType(unit)} />
        <ReadOnly label="Store ID" value={unit.owner?.loginId ?? ''} />
        <ReadOnly label="Uploaded" value={formatDateTime(unit.uploadedAt)} />
        <ReadOnly label="Uploaded By" value={unit.uploadedBy?.loginId ?? ''} />
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, mb: 2, minWidth: 0 }}>
        <Typography component="legend" sx={{ mb: 1, fontWeight: 600 }}>
          Assign to user
        </Typography>
        <Box
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' } }}
        >
          <AgentChainSelect value={chain} onChange={setChain} />
        </Box>
        <FormHelperText error={Boolean(ownerError)}>
          {ownerError ??
            'The unit moves to the last user selected. Leave empty to keep the current owner.'}
        </FormHelperText>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <SelectInput
          control={control}
          name="modelId"
          label="Product Name"
          required
          options={modelOptions}
        />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          required
          options={statusOptions(unit.kind)}
        />
        {statusChanged ? (
          <SelectInput
            control={control}
            name="reasonId"
            label="Change Reason"
            required={REASON_REQUIRED_STATUSES.includes(status)}
            emptyLabel="Select"
            options={(reasons.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
            helperText={reasons.isError ? 'Could not load the change reasons' : undefined}
          />
        ) : null}
        <TextInput
          control={control}
          name="zip"
          label="Zipcode"
          slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
        />
        <TextInput
          control={control}
          name="puk2"
          label="PUK 2"
          slotProps={{ htmlInput: { maxLength: 20 } }}
        />
        <TextInput
          control={control}
          name="poNumber"
          label="PO Number"
          slotProps={{ htmlInput: { maxLength: 50 } }}
        />
        <YesNoInput control={control} name="reservedForPortin" label="Reserve for portin" />
        <SelectInput
          control={control}
          name="planId"
          label="Plan"
          emptyLabel="Select"
          options={(plans.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
        />
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="h3">History</Typography>
      {events.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          No history recorded for this unit.
        </Typography>
      ) : (
        <List dense disablePadding aria-label="Unit history">
          {events.map((e) => (
            <ListItem key={e.id} disableGutters divider>
              <ListItemText
                primary={describeEvent(e)}
                secondary={`${formatDateTime(e.at)} by ${e.actor?.loginId ?? 'system'}${e.batchId ? ' (bulk batch)' : ''}`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </FormDialog>
  );
}

/** "Update Single Unit": identifiers read-only, editable details, and the unit's history. */
export function UnitDialog({ unitId, onClose }: { unitId: string | null; onClose: () => void }) {
  const detail = useUnit(unitId ?? undefined);
  if (!unitId) return null;
  // `key` gives every unit its own form state.
  if (detail.data && detail.data.unit.id === unitId)
    return <UnitForm key={unitId} detail={detail.data} onClose={onClose} />;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{TITLE}</DialogTitle>
      <DialogContent dividers>
        {detail.isError ? (
          <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
        ) : (
          <LoadingState label="Loading unit…" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
